import {
  projectApi,
  type ProjectGoal,
  type ProjectGoalKind,
  type ProjectGoalPathMatch,
} from '@shared/api-client';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { Flag, Plus, Star, Trash2 } from 'lucide-react';
import * as React from 'react';
import { Alert } from '@/components/ui/alert';
import { Badge } from '@/components/ui/badge';
import { Button } from '@/components/ui/button';
import {
  Dialog,
  DialogBody,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '@/components/ui/dialog';
import { Field } from '@/components/ui/field';
import { HelpTooltip } from '@/components/ui/help-tooltip';
import { Input } from '@/components/ui/input';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select';
import { Skeleton } from '@/components/ui/skeleton';
import { toErrorMessage } from '@/hooks/use-form-errors';
import { ConfirmDialog } from '@/pages/settings/components/confirm-dialog';
import { SettingsSection } from '@/pages/settings/components/settings-section';

const triggerLabel = (goal: ProjectGoal) =>
  goal.kind === 'event'
    ? goal.eventName
    : `${goal.pathMatch === 'prefix' ? 'Starts with' : 'Page'} ${goal.pathPattern}`;

const AddGoalDialog = ({
  open,
  onOpenChange,
  projectId,
}: {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  projectId: string;
}) => {
  const queryClient = useQueryClient();
  const [kind, setKind] = React.useState<ProjectGoalKind>('event');
  const [pathMatch, setPathMatch] =
    React.useState<ProjectGoalPathMatch>('exact');
  const [error, setError] = React.useState<string | null>(null);
  const mutation = useMutation({
    mutationFn: (body: Parameters<typeof projectApi.createGoal>[1]) =>
      projectApi.createGoal(projectId, body),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['project-goals', projectId] });
      setError(null);
      onOpenChange(false);
    },
    onError: (cause) => setError(toErrorMessage(cause)),
  });

  React.useEffect(() => {
    if (!open) {
      setKind('event');
      setPathMatch('exact');
      setError(null);
      mutation.reset();
    }
  }, [open]);

  const submit = (event: React.FormEvent<HTMLFormElement>) => {
    event.preventDefault();
    setError(null);
    const form = new FormData(event.currentTarget);
    mutation.mutate({
      name: String(form.get('name') ?? '').trim(),
      kind,
      ...(kind === 'event'
        ? { eventName: String(form.get('eventName') ?? '').trim() }
        : {
            pathPattern: String(form.get('pathPattern') ?? '').trim(),
            pathMatch,
          }),
    });
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent>
        <form onSubmit={submit}>
          <DialogHeader>
            <DialogTitle>Add goal</DialogTitle>
            <DialogDescription>
              Count a meaningful event or visit to a specific page.
            </DialogDescription>
          </DialogHeader>
          <DialogBody className="space-y-4">
            {error ? <Alert tone="error">{error}</Alert> : null}
            <Field label="Goal name" htmlFor="goal-name">
              <Input
                name="name"
                placeholder="Started trial"
                required
                maxLength={80}
                autoFocus
              />
            </Field>
            <Field label="Trigger" htmlFor="goal-kind">
              <Select
                value={kind}
                onValueChange={(value) => setKind(value as ProjectGoalKind)}
              >
                <SelectTrigger id="goal-kind">
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="event">Custom event</SelectItem>
                  <SelectItem value="page">Page visit</SelectItem>
                </SelectContent>
              </Select>
            </Field>
            {kind === 'event' ? (
              <Field
                label="Event name"
                htmlFor="goal-event-name"
                hint={'Track it with gain.track("event_name").'}
              >
                <Input
                  name="eventName"
                  placeholder="trial_started"
                  required
                  maxLength={100}
                  spellCheck={false}
                />
              </Field>
            ) : (
              <div className="grid gap-3 sm:grid-cols-[1fr_9rem]">
                <Field
                  label="Page path"
                  htmlFor="goal-path"
                  hint="Query strings are ignored."
                >
                  <Input
                    name="pathPattern"
                    placeholder="/thank-you"
                    required
                    maxLength={500}
                    spellCheck={false}
                  />
                </Field>
                <Field label="Match" htmlFor="goal-path-match">
                  <Select
                    value={pathMatch}
                    onValueChange={(value) =>
                      setPathMatch(value as ProjectGoalPathMatch)
                    }
                  >
                    <SelectTrigger id="goal-path-match">
                      <SelectValue />
                    </SelectTrigger>
                    <SelectContent>
                      <SelectItem value="exact">Exact</SelectItem>
                      <SelectItem value="prefix">Starts with</SelectItem>
                    </SelectContent>
                  </Select>
                </Field>
              </div>
            )}
          </DialogBody>
          <DialogFooter>
            <Button
              type="button"
              variant="ghost"
              onClick={() => onOpenChange(false)}
            >
              Cancel
            </Button>
            <Button type="submit" loading={mutation.isPending}>
              Add goal
            </Button>
          </DialogFooter>
        </form>
      </DialogContent>
    </Dialog>
  );
};

export const GoalsSection = ({
  projectId,
  canManage,
}: {
  projectId: string;
  canManage: boolean;
}) => {
  const queryClient = useQueryClient();
  const [addOpen, setAddOpen] = React.useState(false);
  const [deleting, setDeleting] = React.useState<ProjectGoal | null>(null);
  const [error, setError] = React.useState<string | null>(null);
  const goals = useQuery({
    queryKey: ['project-goals', projectId],
    queryFn: () => projectApi.listGoals(projectId),
  });
  const refresh = () =>
    queryClient.invalidateQueries({ queryKey: ['project-goals', projectId] });
  const primaryMutation = useMutation({
    mutationFn: (goalId: string) =>
      projectApi.updateGoal(projectId, goalId, { isPrimary: true }),
    onSuccess: () => {
      setError(null);
      void refresh();
    },
    onError: (cause) => setError(toErrorMessage(cause)),
  });
  const deleteMutation = useMutation({
    mutationFn: (goalId: string) => projectApi.deleteGoal(projectId, goalId),
    onSuccess: () => {
      setError(null);
      setDeleting(null);
      void refresh();
    },
    onError: (cause) => setError(toErrorMessage(cause)),
  });

  return (
    <>
      <SettingsSection
        title="Goals"
        description="Choose the customer action that defines conversion on Overview."
        footer={
          canManage ? (
            <Button type="button" variant="secondary" onClick={() => setAddOpen(true)}>
              <Plus />
              Add goal
            </Button>
          ) : undefined
        }
      >
        {goals.isLoading ? (
          <div className="space-y-2">
            <Skeleton className="h-14" />
            <Skeleton className="h-14" />
          </div>
        ) : goals.isError ? (
          <Alert tone="error">Goals could not be loaded.</Alert>
        ) : (
          <div className="divide-y divide-border">
            {error ? <Alert tone="error" className="mb-3">{error}</Alert> : null}
            {(goals.data?.goals ?? []).map((goal) => (
              <div
                key={goal.id}
                className="flex min-h-14 items-center justify-between gap-3 py-3 first:pt-0 last:pb-0"
              >
                <div className="min-w-0">
                  <div className="flex flex-wrap items-center gap-2">
                    <p className="truncate text-sm font-medium text-ink">
                      {goal.name}
                    </p>
                    {goal.isPrimary ? <Badge tone="accent">Primary</Badge> : null}
                  </div>
                  <p className="mt-0.5 truncate font-mono text-xs text-ink-muted">
                    {triggerLabel(goal)}
                  </p>
                </div>
                {canManage ? (
                  <div className="flex shrink-0 items-center gap-1">
                    {!goal.isPrimary ? (
                      <Button
                        type="button"
                        size="sm"
                        variant="ghost"
                        loading={
                          primaryMutation.isPending &&
                          primaryMutation.variables === goal.id
                        }
                        onClick={() => primaryMutation.mutate(goal.id)}
                      >
                        <Star />
                        Make primary
                      </Button>
                    ) : (
                      <HelpTooltip label="Primary goal">
                        This goal supplies the conversion metric on Overview.
                      </HelpTooltip>
                    )}
                    <Button
                      type="button"
                      size="icon"
                      variant="ghost"
                      aria-label={`Delete ${goal.name}`}
                      disabled={goal.isPrimary}
                      onClick={() => setDeleting(goal)}
                    >
                      <Trash2 />
                    </Button>
                  </div>
                ) : null}
              </div>
            ))}
            {!goals.data?.goals.length ? (
              <div className="flex items-center gap-3 py-2 text-sm text-ink-muted">
                <Flag className="size-4" />
                Add a goal to measure conversion.
              </div>
            ) : null}
          </div>
        )}
      </SettingsSection>

      <AddGoalDialog
        open={addOpen}
        onOpenChange={setAddOpen}
        projectId={projectId}
      />
      <ConfirmDialog
        open={Boolean(deleting)}
        onOpenChange={(open) => !open && setDeleting(null)}
        title={`Delete ${deleting?.name ?? 'goal'}?`}
        description="Historical events remain stored, but this conversion will disappear from GainSignal reports."
        confirmLabel="Delete goal"
        loading={deleteMutation.isPending}
        onConfirm={() => {
          if (deleting) {
            deleteMutation.mutate(deleting.id);
          }
        }}
      />
    </>
  );
};
